import ContextMenu from "../context-menu/ContextMenu";
import { ActionItem } from "../context-menu/PopoverList";
import { FC, useCallback, useMemo, useState } from "react";
import Permission from "../../auth/Permission";

type SortDirection = "asc" | "desc";

type Props = {
  headers: Record<string, string>;
  data: Record<string, unknown>[];
  actions?: ActionItem[];
  renders?: Record<
    string,
    (data: unknown, context: unknown) => JSX.Element | FC | string | number
  >;
  subtitles?: Record<string, string>;
  entity?: string;
  role?: string;
  rowClassName?: (data: Record<string, unknown>) => string;
  setFilters?: (filters: { orderBy: string }) => void;
  orderBy?: string;
  sortFields?: Record<string, string>;
};

function GenericTable(props: Props) {
  const controlledSortConfig = useMemo<{
    key: string | null;
    direction: SortDirection;
  } | null>(() => {
    if (!props.orderBy) return null;

    const primaryOrderBy = props.orderBy.split(",")[0]?.trim();
    if (!primaryOrderBy) return null;

    const direction: SortDirection = primaryOrderBy.startsWith("-")
      ? "desc"
      : "asc";
    const sortField = primaryOrderBy.replace(/^-/, "");
    const mappedKey =
      Object.entries(props.sortFields || {}).find(
        ([, field]) => field === sortField
      )?.[0] ?? (sortField in props.headers ? sortField : null);

    return { key: mappedKey, direction };
  }, [props.headers, props.orderBy, props.sortFields]);

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: SortDirection;
  }>(controlledSortConfig ?? { key: null, direction: "asc" });

  const activeSortConfig = controlledSortConfig ?? sortConfig;

  const getSortField = useCallback(
    (key: string) => props.sortFields?.[key] ?? key,
    [props.sortFields]
  );

  const renterItem = useCallback(
    (key, value, data) => {
      return props.renders && props.renders[key]
        ? props.renders[key](value, data)
        : value;
    },
    [props]
  );

  const handleSort = (key: string) => {
    const direction =
      activeSortConfig.key === key && activeSortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({ key, direction });

    if (props.setFilters) {
      const sortField = getSortField(key);
      props.setFilters({
        orderBy: direction === "asc" ? sortField : `-${sortField}`,
      });
    }
  };

  return (
    <div className="generic-table">
      <table className="generic-table-style">
        <thead className="generic-table-thead">
          <tr>
            {Object.entries(props.headers).map(([key, title], index) => (
              <th key={key + index} className="generic-table-header">
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-1 border-0 bg-transparent p-0 text-inherit"
                  onClick={() => handleSort(key)}
                >
                  <span className="generic-table-header-title">
                    {title}
                    {props.subtitles && props.subtitles[key] && (
                      <div className="generic-table-subtitle">
                        {props.subtitles[key]}
                      </div>
                    )}
                  </span>
                  <span
                    className="generic-table-header-title"
                    aria-hidden="true"
                  >
                    {activeSortConfig.key === key
                      ? activeSortConfig.direction === "asc"
                        ? " ↑"
                        : " ↓"
                      : ""}
                  </span>
                </button>
              </th>
            ))}
            <Permission
              role={props.role}
              permission="menu"
              entity={props.entity}
            >
              <th className="generic-table-header">
                {props.actions && <div className="generic-table-header-title">Ações</div>}
              </th>
            </Permission>
          </tr>
        </thead>
        <tbody className="bg-white">
          {props?.data?.length === 0 ? (
            <tr className={"bg-white grid-cols-2"}>
              <td
                className="py-3 text-center"
                colSpan={
                  Object.keys(props.headers).length + (props.actions ? 1 : 0)
                }
              >
                Nenhum registro encontrado.
              </td>
            </tr>
          ) : (
            props?.data?.map((item, index) => (
              <tr
                key={String(item._id ?? item.id ?? index)}
                className={`${index % 2 ? "bg-gray2/10" : "bg-white"} ${
                  props.rowClassName ? props.rowClassName(item) : "text-black"
                }`}
              >
                {Object.keys(props.headers).map((key, index) => (
                  <td
                    key={`${String(item._id ?? item.id ?? index)}-${key}-${index}`}
                    className="generic-table-th dark:text-black"
                  >
                    {renterItem(key, item[key], item)}
                  </td>
                ))}
                <Permission
                  role={props.role}
                  permission="menu"
                  entity={props.entity}
                >
                  <td className="generic-table-th">
                    {props.actions && (
                      <ContextMenu data={item} actions={props.actions} />
                    )}
                  </td>
                </Permission>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default GenericTable;
